<script setup>
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';

defineProps({
  campaigns: {
    type: Array,
    required: true,
  },
  selectedId: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['select', 'delete', 'send']);

const { t } = useI18n();

const getStatusBadgeClass = status => {
  const statusMap = {
    draft: 'bg-n-slate-3 text-n-slate-11',
    scheduled: 'bg-n-blue-3 text-n-blue-11',
    processing: 'bg-n-yellow-3 text-n-yellow-11',
    sent: 'bg-n-green-3 text-n-green-11',
    paused: 'bg-n-orange-3 text-n-orange-11',
    failed: 'bg-n-red-3 text-n-red-11',
  };
  return statusMap[status] || statusMap.draft;
};

const getStatusLabel = status => {
  return t(`CAMPAIGN.BUHOTEXT.STATUS.${status.toUpperCase()}`);
};

const formatDate = date => {
  if (!date) return '--';
  return new Date(date).toLocaleDateString();
};

const canSend = campaign => {
  return campaign.status === 'draft' || campaign.status === 'scheduled';
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="text-sm font-medium text-n-slate-12 mb-2">
      {{ t('CAMPAIGN.BUHOTEXT.CAMPAIGNS_LIST') }}
      <span class="text-n-slate-11 font-normal">({{ campaigns.length }})</span>
    </div>

    <div
      v-for="campaign in campaigns"
      :key="campaign.id"
      class="group relative p-4 rounded-lg border transition-all cursor-pointer"
      :class="
        selectedId === campaign.id
          ? 'bg-n-blue-3 border-n-blue-9'
          : 'bg-n-alpha-3 border-n-weak hover:border-n-blue-6'
      "
      @click="emit('select', campaign)"
    >
      <div class="flex items-start justify-between mb-2">
        <h3 class="font-medium text-n-slate-12 truncate pr-2">
          {{ campaign.name }}
        </h3>
        <span
          :class="getStatusBadgeClass(campaign.status)"
          class="px-2 py-0.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0"
        >
          {{ getStatusLabel(campaign.status) }}
        </span>
      </div>

      <div class="text-sm text-n-slate-11 space-y-1">
        <div class="flex items-center gap-2">
          <i class="i-lucide-smartphone text-xs" />
          <span>{{ campaign.device_code || '--' }}</span>
        </div>
        <div class="flex items-center gap-2">
          <i class="i-lucide-calendar text-xs" />
          <span>{{ formatDate(campaign.scheduled_at) }}</span>
        </div>
        <div
          v-if="campaign.labels_filter?.length"
          class="flex items-center gap-2"
        >
          <i class="i-lucide-tag text-xs" />
          <span class="truncate">{{ campaign.labels_filter.join(', ') }}</span>
        </div>
      </div>

      <!-- Acciones rápidas -->
      <div
        class="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1"
        @click.stop
      >
        <Button
          v-if="canSend(campaign)"
          v-tooltip.top="$t('CAMPAIGN.BUHOTEXT.SEND.TOOLTIP')"
          icon="i-lucide-send"
          slate
          xs
          @click="emit('send', campaign.id)"
        />
        <Button
          v-tooltip.top="$t('CAMPAIGN.BUHOTEXT.DELETE.TOOLTIP')"
          icon="i-lucide-trash-2"
          destructive
          xs
          @click="emit('delete', campaign.id)"
        />
      </div>
    </div>
  </div>
</template>
